import { html, nothing } from "lit";
import { describeSubagentRun } from "../chat/session-tree.ts";
import { subagentRunAgentLine, subagentRunTitle } from "../chat/subagent-run-display.ts";
import {
  resolveSubagentActivityPhase,
  subagentPhaseBadgeClass,
  subagentPhaseLabel,
} from "../chat/subagent-run-status.ts";
import { SUBAGENT_HINTS } from "../control-hints.ts";
import type { ChatSubagentCard } from "../ui-types.ts";

function formatAge(ts: number): string {
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 60) {
    return `${secs}s`;
  }
  const mins = Math.floor(secs / 60);
  if (mins < 60) {
    return `${mins}m`;
  }
  const hrs = Math.floor(mins / 60);
  return `${hrs}h`;
}

function shortSessionId(key: string): string {
  return key.split(":").slice(-2).join(":") || key;
}

export type FleetSubagentRunCardProps = {
  card: ChatSubagentCard;
  expanded: boolean;
  onToggle: () => void;
  onOpenSessionTab?: () => void;
  /**
   * Tree-positioning affordances (optional). When `depth > 0` the
   * card is rendered as a child of a parent subagent; we add a
   * connecting guide line and the parent's short id so the operator
   * can trace the relationship at a glance.
   */
  depth?: number;
  isLastChild?: boolean;
  /** Parent node id (stable: childSessionKey || runId || id of the parent). */
  parentId?: string | null;
  /** Human-readable label for the parent (truncated task / label / id). */
  parentLabel?: string | null;
  /**
   * True when this card is a child of the currently-selected parent.
   * Drives a visible highlight so the operator can spot "everything
   * this parent spawned" without leaving the panel.
   */
  isHighlightedChild?: boolean;
  /**
   * Optional click handler on the *header* of the card. When present
   * (and the card is a parent in the tree), the header button is
   * rendered with an `aria-current` marker so a click can also
   * toggle selection of the parent.
   */
  onSelectParent?: (() => void) | null;
  isParentSelected?: boolean;
};

export function renderFleetSubagentRunCard(props: FleetSubagentRunCardProps) {
  const c = props.card;
  const phase = resolveSubagentActivityPhase(c);
  const active = phase === "ongoing";
  const badge = subagentPhaseBadgeClass(phase);
  // Plain-language summary: what it was asked, what it is doing, and the key
  // finding. The wire-level detail (session keys, lifecycle events) stays
  // behind the card's Debug disclosure.
  const summary = describeSubagentRun(c);
  const depth = Math.max(0, props.depth ?? 0);
  const isLast = Boolean(props.isLastChild);
  const showParentBadge = depth > 0 && Boolean(props.parentId);
  const isHighlighted = Boolean(props.isHighlightedChild);
  const isParentSelected = Boolean(props.isParentSelected);

  const articleClasses = [
    "fleet-map-node",
    "fleet-subagent-run-card",
    active ? "fleet-map-node--active" : "",
    `fleet-subagent-run-card--${phase}`,
    isHighlighted ? "fleet-subagent-run-card--child-highlighted" : "",
    isParentSelected ? "fleet-subagent-run-card--parent-selected" : "",
    showParentBadge ? "fleet-subagent-run-card--child" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return html`
    <article
      class=${articleClasses}
      data-run-phase=${phase}
      data-depth=${depth}
      data-is-last-child=${isLast ? "true" : "false"}
      data-parent-id=${props.parentId ?? ""}
      aria-label=${`${subagentRunTitle(c)}, ${subagentPhaseLabel(phase)}`}
      aria-current=${isParentSelected ? "true" : "false"}
    >
      <button
        type="button"
        class="fleet-subagent-run-card__head"
        @click=${props.onToggle}
        aria-expanded=${props.expanded}
      >
        <div class="fleet-map-node__header">
          <span class="fleet-map-node__title">${subagentRunTitle(c)}</span>
          <span class="fleet-map-node__badge fleet-map-node__badge--${badge}">
            ${subagentPhaseLabel(phase)}
          </span>
        </div>
        <p class="fleet-map-node__meta">${subagentRunAgentLine(c)}</p>
        <p
          class="fleet-subagent-run-card__asked muted"
          data-testid="fleet-subagent-run-card__asked"
          title=${summary.asked}
        >
          ${summary.asked}
        </p>
        ${
          showParentBadge
            ? html`
                <p
                  class="fleet-subagent-run-card__parent-badge mono"
                  title=${`Parent session: ${props.parentId}`}
                >
                  <span class="fleet-subagent-run-card__parent-icon" aria-hidden="true">↳</span>
                  <span class="fleet-subagent-run-card__parent-label"
                    >${props.parentLabel ?? shortSessionId(props.parentId ?? "")}</span
                  >
                  <span class="fleet-subagent-run-card__parent-id muted"
                    >${shortSessionId(props.parentId ?? "")}</span
                  >
                </p>
              `
            : nothing
        }
        ${
          c.toolHint && active
            ? html`<p class="fleet-subagent-run-card__hint muted">${c.toolHint}</p>`
            : nothing
        }
        <span class="fleet-subagent-run-card__chevron muted" aria-hidden="true">
          ${props.expanded ? "▼" : "▶"}
        </span>
      </button>

      ${
        props.expanded
          ? html`
              <div class="fleet-subagent-run-card__body">
                <p class="fleet-map-node__task">${c.task}</p>
                ${
                  summary.finding
                    ? html`<p
                        class="fleet-subagent-run-card__finding"
                        data-testid="fleet-subagent-run-card__finding"
                      >
                        ${summary.finding}
                      </p>`
                    : nothing
                }
                ${
                  c.error
                    ? html`<div class="callout danger fleet-subagent-run-card__err">
                        ${c.error}
                      </div>`
                    : nothing
                }
                ${
                  c.streamText
                    ? html`
                        <pre class="fleet-subagent-run-card__stream mono muted" aria-live="polite">
${c.streamText.slice(-4000)}</pre>
                      `
                    : nothing
                }
                ${
                  c.events && c.events.length > 0
                    ? html`
                        <details class="fleet-subagent-run-card__debug">
                          <summary class="muted">
                            Debug — raw run events (${c.events.length})
                          </summary>
                          <ul class="fleet-subagent-run-card__debug-list mono">
                            ${c.events.map(
                              (event) => html`
                                <li class="fleet-subagent-run-card__debug-row">
                                  <span class="fleet-subagent-run-card__debug-seq"
                                    >#${event.seq}</span
                                  >
                                  <span class="fleet-subagent-run-card__debug-stream"
                                    >${event.stream}</span
                                  >
                                  <span class="fleet-subagent-run-card__debug-phase"
                                    >${event.phase ?? ""}</span
                                  >
                                </li>
                              `,
                            )}
                          </ul>
                        </details>
                      `
                    : nothing
                }
                <div class="fleet-map-node__actions">
                  ${
                    c.childSessionKey && props.onOpenSessionTab
                      ? html`
                          <button
                            type="button"
                            class="fleet-map-node__btn fleet-map-node__btn--open"
                            title=${SUBAGENT_HINTS.openSessionTab(c.label)}
                            @click=${(e: Event) => {
                              e.stopPropagation();
                              props.onOpenSessionTab?.();
                            }}
                          >
                            Open session
                          </button>
                        `
                      : nothing
                  }
                  ${
                    // Focus-children toggle: only present when the renderer
                    // identified this card as a tree parent (i.e. at least
                    // one in-scope child card has this card as
                    // parentSessionKey). Clicking flips the
                    // `selectedSubagentParentId` state in app.ts, which
                    // tints every descendant of this parent.
                    props.onSelectParent
                      ? html`
                          <button
                            type="button"
                            class="fleet-map-node__btn fleet-map-node__btn--focus-children ${isParentSelected ? "fleet-map-node__btn--active" : ""}"
                            data-testid="subagent-focus-children"
                            title=${
                              isParentSelected
                                ? "Clear focus from this parent's children"
                                : "Highlight every child spawned by this subagent"
                            }
                            aria-pressed=${isParentSelected ? "true" : "false"}
                            @click=${(e: Event) => {
                              e.stopPropagation();
                              props.onSelectParent?.();
                            }}
                          >
                            ${isParentSelected ? "Unfocus children" : "Focus children"}
                          </button>
                        `
                      : nothing
                  }
                  ${
                    c.updatedAt
                      ? html`<span class="muted fleet-subagent-run-card__age"
                          >Updated ${formatAge(c.updatedAt)} ago</span
                        >`
                      : nothing
                  }
                </div>
              </div>
            `
          : nothing
      }
    </article>
  `;
}
